import React, { ReactNode } from 'react'
import { Item, Section } from 'react-stately'
import styled from 'styled-components'
import { Box, Flex } from '../../../core-components'
import { StartContentProvider } from '../../data'
import { CascadeSelectItemPublicComponent, SelectItemPublicComponent } from './publicComponentTypes'

export type DynamicSectionItem = {
  children: readonly (DynamicCascadeItem | DynamicSimpleItem)[]
  itemType: 'section'
  title: string
}
export type DynamicCascadeItem = {
  children: ReactNode
  dataId?: string
  dataTestId?: string
  description?: string
  endContent?: ReactNode
  isDisabled?: boolean
  itemType: 'cascade'
  label: string
  startContent?: ReactNode
  value: string
}
export type DynamicSimpleItem = {
  children: ReactNode
  dataId?: string
  dataTestId?: string
  description?: string
  endContent?: ReactNode
  isDisabled?: boolean
  itemType: 'item'
  startContent?: ReactNode
  textValue?: string
  value: string
}
export type DynamicItem = DynamicSectionItem | DynamicCascadeItem | DynamicSimpleItem
type selectItemPublicToDynamicItemFunction = {
  (fake: SelectItemPublicComponent, type: 'item'): DynamicSimpleItem
  (
    fake: { children: readonly (DynamicCascadeItem | DynamicSimpleItem)[]; title: string },
    type: 'section'
  ): DynamicSectionItem
  (fake: CascadeSelectItemPublicComponent, type: 'cascade'): DynamicCascadeItem
}
/** Maps public SelectItem interface to object to be passed to dynamicItemToItem */
export const selectItemPublicToDynamicItem = ((
  ...[fake, type]:
    | [SelectItemPublicComponent, 'item']
    | [{ children: readonly (DynamicCascadeItem | DynamicSimpleItem)[]; title: string }, 'section']
    | [CascadeSelectItemPublicComponent, 'cascade']
) => {
  if (type === 'section') {
    const { children, title } = fake

    return {
      itemType: 'section' as const,
      children,
      title,
    }
  }
  if (type === 'cascade') {
    const {
      'data-id': dataId,
      'data-test-id': dataTestId,
      value,
      label,
      children,
      description,
      isDisabled,
      endContent,
      startContent,
    } = fake.props

    return {
      itemType: 'cascade' as const,
      dataId,
      dataTestId,
      value,
      children,
      label,
      description,
      isDisabled,
      endContent,
      startContent,
    }
  }

  const {
    'data-id': dataId,
    'data-test-id': dataTestId,
    value,
    children,
    textValue,
    endContent,
    startContent,
    description,
    isDisabled,
  } = fake.props

  if (typeof children === 'string') {
    return {
      itemType: 'item' as const,
      value,
      textValue: children,
      children,
      endContent,
      startContent,
      description,
      dataId,
      dataTestId,
      isDisabled,
    }
  }

  return {
    itemType: 'item' as const,
    value,
    textValue,
    children,
    dataId,
    dataTestId,
    isDisabled,
  }
}) as selectItemPublicToDynamicItemFunction

const itemDataToItem = (itemData: DynamicSimpleItem) => {
  // Item's children become `item.rendered` used in
  // the select list and in the select button when item is selected
  const { children, startContent, textValue, value } = itemData
  if (typeof children === 'string') {
    return (
      <Item key={value} textValue={children}>
        <Flex
          alignItems='center'
          flexGrow={1}
          gap={2}
          minWidth={0 /* Needed for overflow ellipsis */}
        >
          {startContent && (
            <StartContentProvider value={{ iconColor: 'fg/minor' }}>
              {startContent}
            </StartContentProvider>
          )}
          <BoxWithEllipsis
            lineHeight={
              8 /* Overflow hidden clips the text otherwise, use negative margin to offset it */
            }
            my={-1}
          >
            {children}
          </BoxWithEllipsis>
        </Flex>
      </Item>
    )
  }
  return (
    <Item key={value} textValue={textValue}>
      <Box flexGrow={1} minWidth={0}>
        {children}
      </Box>
    </Item>
  )
}

/** Maps dynamic item data to the actual react-aria's item */
export const dynamicItemToItem = (itemData: DynamicItem) => {
  if (itemData.itemType === 'section') {
    const { children, title } = itemData

    return (
      <Section key={`${title}_section`} title={title} items={children}>
        {dynamicItemToItem}
      </Section>
    )
  }

  if (itemData.itemType === 'cascade') {
    return itemDataToItem({
      children: itemData.label,
      dataId: itemData.dataId,
      dataTestId: itemData.dataTestId,
      description: itemData.description,
      isDisabled: itemData.isDisabled,
      itemType: 'item',
      textValue: itemData.value,
      value: itemData.value,
      startContent: itemData.startContent,
    })
  }

  return itemDataToItem(itemData)
}

const BoxWithEllipsis = styled(Box)`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`
